// TitleBarSettings — the authoring pane for the Title Bar block, wired as
// TitleBar.craft.related.settings. Uses the app's SHIPPED editor controls, not
// bespoke ones (everything-is-a-shared-component):
//   HEADING · Title      — shared <TextInputField>.
//            · Font       — shared <FontSelect> (the curated 9-font list; deck's
//                           "Font type DM Sans" shared component).
//            · Size       — <SegChoice> S · M · L  (token type scale).
//            · Alignment  — <SegChoice> Left · Center · Right.
//   BAR     · Height      — <SegChoice> S · M · L (deck Size Height, M = 52px).
//            · Background  — the REUSED Container <ContainerBackgroundControl>
//                           (#1996): Image/GIF · Color · Gradient, unchanged.
//   ADVANCED· Text tone   — <SegChoice> Auto · Light · Dark (deck's auto-flip).
//
// DECK-15 DELTA (flag TITLE_BAR_DECK15_ENABLED — OFF renders exactly the fields
// listed above, nothing else mounts): Text size gains a "Custom" choice with a
// px slider · the 1-D Alignment control is replaced by the shared 3x3 <PosGrid>
// "Text position" (writing `align` too, so it stays forward-safe) · Bar gains
// Height "Custom", Width Full/75%/50% and Corner rounding None/S/M/Full ·
// Advanced gains the Overlay scope (Block behind text / Shadow behind text /
// Whole bar) with the Darken<->Lighten slider. Every one of these is a SHARED
// control (SegChoice / PosGrid / LabeledSlider) — no bespoke widget.
//
// Reads/writes node props via useNode()/setProp. The background control is
// controlled (value/onPatch): `value` mirrors the Container shape it expects
// ({ background, opacity, containerFill }) and `onPatch` merges partial patches
// onto the node props — so a "Color / Gradient / Image" choice persists as the
// SAME `containerFill` prop the shared resolver reads at render time.

import { useNode } from "@craftjs/core";
import React from "react";
import styled from "styled-components";
import { fontFamily } from "../../../../theme/tokens";
import EditorSection from "../../BlockEditorPane/EditorSection";
import AdvancedOptions from "../../BlockEditorPane/AdvancedOptions";
import {
  TextInputField,
  SegChoice,
  FontSelect,
  PosGrid,
  LabeledSlider,
  QuadStepper,
} from "../../BlockEditorPane/controls";
import ContainerBackgroundControl from "../Container/ContainerBackgroundControl";
import TitleBarCropControl from "./TitleBarCropControl";
import { TITLE_BAR_DECK15_ENABLED } from "../../../../constants/featureFlags";
import {
  CUSTOM,
  CUSTOM_HEIGHT_RANGE,
  CUSTOM_SIZE_RANGE,
  ADJUST_RANGE,
  alignToPosition,
  positionToAlign,
} from "./titleBarDeck15";

// The SHARED pattern library (45 deck motifs + scale + line-art/background color
// sources + the canonical red-slash Transparent swatch). Lazy: it is a heavy,
// browser-only subtree, exactly like the gradient/media pickers beside it.
const BackgroundPatternPanel = React.lazy(() =>
  import("../../../backgroundPatterns/BackgroundPatternPanel")
);

// Deck-15's fourth bar-background source. Opt-in tab on the shared control.
const PATTERN_TAB = [{ value: "pattern", label: "Patterns" }];

const Pane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: ${fontFamily};
  box-sizing: border-box;
  & * {
    box-sizing: border-box;
  }
`;

const HelpText = styled.p`
  margin: 0;
  font-family: ${fontFamily};
  font-size: 12px;
  font-style: italic;
  color: #667085;
  line-height: 1.4;
`;

// Mirrors TableEditor's NewBadge (same Build-Menu-wave "NEW" tag, same
// background/color/radius) so Title Bar's pane heading reads as the same
// family as Table's instead of showing no tag at all.
const NewBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 6px;
  background: #ede4fe;
  color: #6c1fb8;
  font-family: ${fontFamily};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  vertical-align: middle;
`;

const SIZE_OPTIONS = [
  { label: "S", value: "s" },
  { label: "M", value: "m" },
  { label: "L", value: "l" },
];
const ALIGN_OPTIONS = [
  { label: "Left", value: "left" },
  { label: "Center", value: "center" },
  { label: "Right", value: "right" },
];
const TONE_OPTIONS = [
  { label: "Auto", value: "auto" },
  { label: "Light", value: "light" },
  { label: "Dark", value: "dark" },
];

// ── DECK-15 DELTA options (deck labels verbatim) ─────────────────────────────
const CUSTOM_OPTION = { label: "Custom", value: CUSTOM };
const SIZE_OPTIONS_WITH_CUSTOM = [...SIZE_OPTIONS, CUSTOM_OPTION];
const WIDTH_OPTIONS = [
  { label: "Full", value: "full" },
  { label: "75%", value: "75" },
  { label: "50%", value: "50" },
];
const ROUNDING_OPTIONS = [
  { label: "None", value: "none" },
  { label: "S", value: "s" },
  { label: "M", value: "m" },
  { label: "Full", value: "full" },
];
const OVERLAY_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Block behind text", value: "block" },
  { label: "Shadow behind text", value: "shadow" },
  { label: "Whole bar", value: "bar" },
];

const TitleBarSettings = () => {
  const {
    actions: { setProp },
    title,
    fontFamily: fontProp,
    size,
    align,
    height,
    textTone,
    background,
    opacity,
    containerFill,
    cornerRounding,
    barWidth,
    position,
    overlayScope,
    overlayAdjust,
    customHeightPx,
    customSizePx,
    barCrop,
    margin,
  } = useNode((node) => ({
    title: node.data.props.title,
    fontFamily: node.data.props.fontFamily,
    size: node.data.props.size,
    align: node.data.props.align,
    height: node.data.props.height,
    textTone: node.data.props.textTone,
    background: node.data.props.background,
    opacity: node.data.props.opacity,
    containerFill: node.data.props.containerFill,
    cornerRounding: node.data.props.cornerRounding,
    barWidth: node.data.props.barWidth,
    position: node.data.props.position,
    overlayScope: node.data.props.overlayScope,
    overlayAdjust: node.data.props.overlayAdjust,
    customHeightPx: node.data.props.customHeightPx,
    customSizePx: node.data.props.customSizePx,
    barCrop: node.data.props.barCrop,
    margin: node.data.props.margin,
  }));

  const set = (key) => (value) => setProp((props) => (props[key] = value));

  const setMarginSide = (side, value) =>
    setProp((props) => {
      props.margin = { top: 0, right: 0, bottom: 0, left: 0, ...props.margin, [side]: value };
    });

  // The 3x3 grid supersedes the 1-D Left/Center/Right control, so it keeps the
  // legacy `align` prop in sync — a bar authored with the grid still renders
  // correctly if the flag is ever turned back OFF (forward-safe).
  const setPosition = (value) =>
    setProp((props) => {
      props.position = value;
      props.align = positionToAlign(value);
    });

  const effectivePosition = position || alignToPosition(align || "center");

  // The crop tool only has something to crop when an IMAGE fill is chosen.
  const imageSrc =
    (containerFill &&
      containerFill.type === "image" &&
      containerFill.image &&
      containerFill.image.url) ||
    "";

  // Merge a partial patch onto the node props — the contract
  // ContainerBackgroundControl expects (it emits { background } / { opacity } /
  // { containerFill } / { containerFill: null } patches).
  const patchBackground = (patch) =>
    setProp((props) => {
      Object.keys(patch).forEach((k) => {
        props[k] = patch[k];
      });
    });

  return (
    <Pane>
      <EditorSection heading={<span>Heading<NewBadge>NEW</NewBadge></span>}>
        <HelpText>The heading shown on the bar.</HelpText>
        <TextInputField
          label="Title"
          value={title || ""}
          placeholder="e.g. Client wins"
          maxLength={120}
          onChange={set("title")}
        />
        <FontSelect value={fontProp || "DM Sans"} onChange={set("fontFamily")} />
        <SegChoice
          // Deck-15 names this field "Text size"; the shipped pane says "Size".
          // Flag OFF keeps the shipped copy byte-identical.
          label={TITLE_BAR_DECK15_ENABLED ? "Text size" : "Size"}
          value={size || "m"}
          options={TITLE_BAR_DECK15_ENABLED ? SIZE_OPTIONS_WITH_CUSTOM : SIZE_OPTIONS}
          onChange={set("size")}
        />
        {TITLE_BAR_DECK15_ENABLED && size === CUSTOM && (
          <LabeledSlider
            label="Custom text size"
            value={customSizePx ?? CUSTOM_SIZE_RANGE.fallback}
            min={CUSTOM_SIZE_RANGE.min}
            max={CUSTOM_SIZE_RANGE.max}
            unit=" px"
            onChange={set("customSizePx")}
          />
        )}
        {TITLE_BAR_DECK15_ENABLED ? (
          <PosGrid
            label="Text position"
            value={effectivePosition}
            onChange={setPosition}
          />
        ) : (
          <SegChoice
            label="Alignment"
            value={align || "center"}
            options={ALIGN_OPTIONS}
            onChange={set("align")}
          />
        )}
      </EditorSection>

      <EditorSection heading="Bar">
        <SegChoice
          label="Height"
          value={height || "m"}
          options={TITLE_BAR_DECK15_ENABLED ? SIZE_OPTIONS_WITH_CUSTOM : SIZE_OPTIONS}
          onChange={set("height")}
        />
        {TITLE_BAR_DECK15_ENABLED && height === CUSTOM && (
          <LabeledSlider
            label="Custom height"
            value={customHeightPx ?? CUSTOM_HEIGHT_RANGE.fallback}
            min={CUSTOM_HEIGHT_RANGE.min}
            max={CUSTOM_HEIGHT_RANGE.max}
            unit=" px"
            onChange={set("customHeightPx")}
          />
        )}
        {TITLE_BAR_DECK15_ENABLED && (
          <>
            <SegChoice
              label="Width"
              value={barWidth || "full"}
              options={WIDTH_OPTIONS}
              onChange={set("barWidth")}
            />
            <SegChoice
              label="Corner rounding"
              value={cornerRounding || "m"}
              options={ROUNDING_OPTIONS}
              onChange={set("cornerRounding")}
            />
          </>
        )}
        {TITLE_BAR_DECK15_ENABLED ? (
          <>
            <ContainerBackgroundControl
              value={{ background, opacity, containerFill }}
              onPatch={patchBackground}
              // Deck-15 adds Patterns to the bar's background sources. The tab
              // is OPT-IN on the shared control, so Container is untouched.
              extraTypes={PATTERN_TAB}
              renderExtra={() => (
                <React.Suspense fallback={<HelpText>Loading patterns…</HelpText>}>
                  <BackgroundPatternPanel
                    value={(containerFill && containerFill.css) || ""}
                    onApply={(css) =>
                      patchBackground({ containerFill: { type: "pattern", css } })
                    }
                  />
                </React.Suspense>
              )}
            />
            <TitleBarCropControl
              src={imageSrc}
              crop={barCrop}
              onChange={set("barCrop")}
            />
          </>
        ) : (
          <ContainerBackgroundControl
            value={{ background, opacity, containerFill }}
            onPatch={patchBackground}
          />
        )}
      </EditorSection>

      <AdvancedOptions>
        {TITLE_BAR_DECK15_ENABLED && (
          <>
            <SegChoice
              label="Overlay"
              value={overlayScope || "none"}
              options={OVERLAY_OPTIONS}
              onChange={set("overlayScope")}
            />
            {overlayScope && overlayScope !== "none" && (
              <>
                <LabeledSlider
                  label="Darken ↔ Lighten"
                  value={overlayAdjust ?? 0}
                  min={ADJUST_RANGE.min}
                  max={ADJUST_RANGE.max}
                  onChange={set("overlayAdjust")}
                />
                <HelpText style={{ marginTop: 6, marginBottom: 6 }}>
                  Negative darkens, positive lightens. Block behind text rounds
                  with the bar&apos;s corner rounding.
                </HelpText>
              </>
            )}
          </>
        )}
        <SegChoice
          label="Text tone"
          value={textTone || "auto"}
          options={TONE_OPTIONS}
          onChange={set("textTone")}
        />
        <HelpText style={{ marginTop: 6 }}>
          Auto flips the title light or dark to stay readable on your background.
        </HelpText>
        {/* fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458:
            reuses the SAME QuadStepper Text/Image already use for their
            Padding/Margin control (BlockEditorPane/controls.jsx). */}
        <QuadStepper
          label="Margin"
          value={margin || { top: 0, right: 0, bottom: 0, left: 0 }}
          onChange={setMarginSide}
        />
      </AdvancedOptions>
    </Pane>
  );
};

export default TitleBarSettings;
